import { faArrowDown, faArrowUp, faChevronRight } from '@fortawesome/free-solid-svg-icons';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { useState } from 'react';
import { useFormContext, useFieldArray } from 'react-hook-form';
import ImageSelector from './ImageSelector';
import { FormField } from '../../../../components/FormField';
import Expandable from '../../../../components/Expandable';
import RichMdxEditor from '../../../../components/RichMdxEditor';
import LabeledImageEditor from '../../../../components/LabeledImageEditor';

function LabeledImageRow({
  index,
  title,
  selectedImage,
  onDelete,
  onMove,
  isFirst,
  isLast,
  animationDirection
}: {
  index: number;
  title: string;
  selectedImage?: any;
  onDelete: () => void;
  onMove: (direction: 'up' | 'down') => void;
  isFirst: boolean;
  isLast: boolean;
  animationDirection?: 'up' | 'down' | null;
}) {
  const header = (
    <div
      style={{
        padding: '12px',
        display: 'flex',
        alignItems: 'center',
        gap: '0.75rem',
      }}
    >
      <span
        style={{
          fontSize: '0.875rem',
          display: 'inline-flex',
          alignItems: 'center',
          justifyContent: 'center',
          width: '1.25rem',
          color: 'var(--color-primary)',
        }}
      >
        <FontAwesomeIcon icon={faChevronRight} />
      </span>

      {selectedImage?.public_url && (
        <img
          src={selectedImage.public_url}
          alt={selectedImage.label || 'Labeled image'}
          style={{
            width: '40px',
            height: '40px',
            objectFit: 'cover',
            border: '1px solid var(--color-base-300)',
          }}
        />
      )}

      <div style={{ flex: 1, display: 'flex', flexDirection: 'column', gap: '4px' }}>
        <div style={{ fontWeight: 'bold', fontSize: '14px' }}>
          {title || 'New Labeled Image'}
        </div>
      </div>

      <div
        style={{ display: 'flex', gap: '4px' }}
        onClick={(e) => e.stopPropagation()}
      >
        <button
          type="button"
          className="btn btn-sm btn-primary"
          onClick={() => onMove('up')}
          disabled={isFirst}
          title="Move up"
        >
          <FontAwesomeIcon icon={faArrowUp} />
        </button>

        <button
          type="button"
          className="btn btn-sm btn-primary"
          onClick={() => onMove('down')}
          disabled={isLast}
          title="Move down"
        >
          <FontAwesomeIcon icon={faArrowDown} />
        </button>

        <button
          type="button"
          className="btn btn-sm btn-error"
          onClick={onDelete}
          title="Delete"
        >
          Delete
        </button>
      </div>
    </div>
  );

  const content = (
    <FormField.Container
      style={{
        padding: '12px',
        borderTop: '2px solid var(--color-base-300)',
        display: 'flex',
        flexDirection: 'column',
        gap: '12px',
      }}
    >
      <FormField.Field
        name={`labeled_images.${index}.title`}
        label="Title"
        placeholder="Enter a title for this image..."
      />

      <div>
        <label
          style={{
            display: 'block',
            fontSize: '0.875rem',
            fontWeight: 500,
            marginBottom: '0.25rem',
          }}
        >
          Description
        </label>
        <div style={{
          backgroundColor: 'var(--color-white)'
        }}>
          <RichMdxEditor
            key={`labeled-image-${index}-description`}
            name={`labeled_images.${index}.description`}
          />
        </div>
      </div>

      <ImageSelector
        name={`labeled_images.${index}.image`}
      />

      <div>
        <label
          style={{
            display: 'block',
            fontSize: '0.875rem',
            fontWeight: 500,
            marginBottom: '0.5rem',
          }}
        >
          Image Labels
        </label>
        <LabeledImageEditor name={`labeled_images.${index}`} />
      </div>
    </FormField.Container>
  );

  const getAnimationStyle = () => {
    if (!animationDirection) return {};
    return {
      animation: `slide-${animationDirection} 0.3s ease-out`,
    };
  };

  return (
    <div style={{ marginBottom: '8px', ...getAnimationStyle() }}>
      <Expandable header={header} content={content} />
    </div>
  );
}

// CSS keyframes for animation - injected once
const styleId = 'labeled-images-animations';
if (typeof document !== 'undefined' && !document.getElementById(styleId)) {
  const style = document.createElement('style');
  style.id = styleId;
  style.textContent = `
    @keyframes slide-up {
      0% { transform: translateY(100%); opacity: 0.7; }
      100% { transform: translateY(0); opacity: 1; }
    }
    @keyframes slide-down {
      0% { transform: translateY(-100%); opacity: 0.7; }
      100% { transform: translateY(0); opacity: 1; }
    }
  `;
  document.head.appendChild(style);
}

export default function LabeledImagesEditor() {
  const { control, watch } = useFormContext();
  const { fields, append, remove, move } = useFieldArray({
    control,
    name: "labeled_images"
  });

  const [animatingItems, setAnimatingItems] = useState<Record<string, 'up' | 'down'>>({});

  const addItem = () => {
    append({
      title: '',
      description: '',
      image: null,
      labels: []
    });
  };

  if (fields.length === 0) {
    return (
      <div style={{ padding: '16px', textAlign: 'center', color: 'var(--color-base-content)' }}>
        <p style={{ marginBottom: '12px', opacity: 0.7 }}>No labeled images yet</p>
        <button
          type="button"
          onClick={addItem}
          style={{
            padding: '8px 16px',
            border: '1px solid var(--color-primary)',
            background: 'var(--color-primary)',
            color: 'white',
            cursor: 'pointer',
            fontSize: '14px',
          }}
        >
          + Add Labeled Image
        </button>
      </div>
    );
  }

  return (
    <div>
      {fields.map((field, index) => {
        const title = watch(`labeled_images.${index}.title`);
        const selectedImage = watch(`labeled_images.${index}.image`);
        return (
          <LabeledImageRow
            key={`labeled-image-${index}-${field.id}`}
            index={index}
            title={title}
            selectedImage={selectedImage}
            onDelete={() => remove(index)}
            onMove={(direction) => {
              if (direction === 'up' && index > 0) {
                const otherFieldId = fields[index - 1].id;
                setAnimatingItems({
                  [field.id]: 'up',
                  [otherFieldId]: 'down',
                });
                move(index, index - 1);
                setTimeout(() => setAnimatingItems({}), 300);
              } else if (direction === 'down' && index < fields.length - 1) {
                const otherFieldId = fields[index + 1].id;
                setAnimatingItems({
                  [field.id]: 'down',
                  [otherFieldId]: 'up',
                });
                move(index, index + 1);
                setTimeout(() => setAnimatingItems({}), 300);
              }
            }}
            isFirst={index === 0}
            isLast={index === fields.length - 1}
            animationDirection={animatingItems[field.id] || null}
          />
        );
      })}

      <button
        type="button"
        onClick={addItem}
        style={{
          padding: '8px 16px',
          border: '1px solid var(--color-primary)',
          background: 'var(--color-primary)',
          color: 'white',
          cursor: 'pointer',
          fontSize: '14px',
          width: '100%',
          marginTop: '8px'
        }}
      >
        + Add Labeled Image
      </button>
    </div>
  );
}
